import { collection, doc, getDocs, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';

export interface BilingualText {
  en: string;
  ar: string;
}

export interface ScaleOption {
  id: string;
  text: BilingualText;
  score: number;
}

export interface ScaleQuestion {
  id: string;
  question: BilingualText;
  options: ScaleOption[];
  order: number;
  category: 'Exercises' | 'Nutrition' | 'Sleep' | 'Stress' | 'Relations' | 'Addictions';
}

const COLLECTION_NAME = 'questionnaires'; // Updated to use new collection name

/** A questionnaire's questions in the scale shape the fill screens use. */
export function toScaleQuestions(questionnaire: { questions: ScaleQuestion[] } | null): ScaleQuestion[] {
  return (questionnaire?.questions ?? []).map((q) => ({
    id: q.id,
    question: q.question,
    options: q.options,
    order: q.order,
    category: q.category,
  }));
}

export async function getScaleQuestions(): Promise<ScaleQuestion[]> {
  try {
    // Get the default questionnaire from the new system
    const { getDefaultQuestionnaire } = await import('./questionnaires');
    const defaultQuestionnaire = await getDefaultQuestionnaire();
    
    if (!defaultQuestionnaire) {
      return [];
    }
    
    // Convert questionnaire questions to the legacy format
    return defaultQuestionnaire.questions.map(q => ({
      id: q.id,
      question: q.question,
      options: q.options,
      order: q.order,
      category: q.category
    }));
  } catch (error) {
    console.error('Error fetching scale questions:', error);
    throw error;
  }
}

export async function saveScaleQuestions(questions: ScaleQuestion[]): Promise<void> {
  try {
    // Delete all existing questions
    const questionsRef = collection(db, COLLECTION_NAME);
    const snapshot = await getDocs(questionsRef);
    const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);

    // Save new questions
    const savePromises = questions.map((question, index) => {
      const docRef = doc(db, COLLECTION_NAME, question.id);
      return setDoc(docRef, {
        ...question,
        order: index
      });
    });

    await Promise.all(savePromises);
  } catch (error) {
    console.error('Error saving scale questions:', error);
    throw error;
  }
} 